import { ReactNode, useEffect, useRef, useState } from 'react';
import { runWhenIdle } from '../../utils/idle';

type LazyStrategy = 'in-view' | 'idle';

type LazyRenderProps = {
  children: ReactNode;
  className?: string;
  placeholder?: ReactNode;
  rootMargin?: string;
  minHeight?: number | string;
  strategy?: LazyStrategy;
  idleTimeout?: number;
};

const LazyRender = ({
  children,
  className,
  placeholder = null,
  rootMargin = '300px 0px',
  minHeight,
  strategy = 'in-view',
  idleTimeout = 1200,
}: LazyRenderProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (strategy === 'idle') {
      return runWhenIdle(() => setIsVisible(true), idleTimeout);
    }

    if (strategy !== 'in-view' || isVisible) return;
    if (typeof window === 'undefined') return;

    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    const element = ref.current;
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [strategy, rootMargin, idleTimeout, isVisible]);

  if (strategy === 'idle') {
    return isVisible ? <>{children}</> : <>{placeholder}</>;
  }

  // Reserva uma altura mínima ENQUANTO a seção ainda não montou (placeholder),
  // reduzindo o "pop-in"/CLS no scroll. Como o observer dispara `rootMargin` (300px)
  // ANTES da viewport, a troca reservado->conteúdo ocorre fora da tela → sem shift
  // visível e sem buraco permanente (a reserva some quando o conteúdo aparece).
  const reservedMinHeight = isVisible ? minHeight : (minHeight ?? 240);

  return (
    <div ref={ref} className={className} style={reservedMinHeight ? { minHeight: reservedMinHeight } : undefined}>
      {isVisible ? children : placeholder}
    </div>
  );
};

export default LazyRender;
